import React, {useMemo, useState} from 'react';

import {Card, Block, Dialog, DialogBody, DialogFooter, Button, VF, Space, Title, Col, Row, ModalComponentProps, Callout, Checkbox, Divider} from '@mc/ui-v4';

import {Form, FormRenderProps} from 'react-final-form';
import {AtributyApi, AttributeConfigVO, ConfigApi, EnumIconType, EnumTypLayerAttr, LayerGeometryType, LayerSettingsVO} from '../../../api';
import {getLayerGeometryTypeItems} from '../../../enums/EnumLayerGeometryType';
import {OnChange} from 'react-final-form-listeners';
import {IkonPreview} from '../../ikon/IkonPreview';
import {COLOR_ITEMS, ColorPicker} from '../../ikon/ColorPicker';
import {WaitForQueryData} from '@mc/react-app-v4';
import {useAppConfig} from '@mc/react-app-base';
import {CACHE_KEY_ATRIBUTY, CACHE_KEY_CATALOG_OPRAVNENI} from '../../../queryKeys';
import {useQuery} from 'react-query';
import {formatScaleLabel, nearestScale, resolutionToScale, scaleToResolution} from '../../../helpers/ScaleResolutionUtils';

type Props = ModalComponentProps & {
    title?: string;
    initialValues?: LayerSettingsVO;
    isEdit: boolean;
    isSublayer?: boolean;
    onSubmit: (data: LayerSettingsVO) => void;
    xsdTyp?: string;
};
export const UserLayerModal: React.FC<Props> = ({onClose, isEdit, initialValues, isSublayer = false, title, onSubmit, xsdTyp}) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const appConfig = useAppConfig();
    // @ts-ignore
    const scales: number[] = Array.isArray(appConfig?.scales) ? appConfig.scales : [];

    const scaleItems = scales.map(scale => ({
        id: scale,
        name: formatScaleLabel(scale),
    }));

    const validate = (values: LayerSettingsVO) => {
        const errors: any = {};
        if (!values.nazev && !isSublayer) {
            errors.nazev = 'Název je povinný';
        }
        if (!values.typGeometrie) {
            errors.typGeometrie = 'Typ geometrie je povinný';
        }
        if (values.ikona?.typMarker === EnumIconType.CUSTOM) {
            if (!values.barva) {
                errors.barva = 'Barva je povinná';
            }
            if (!values.ikona.ikonaText) {
                errors.ikona = {ikonaText: 'Text musí mít minimálně 1 znak'};
            }
            if (values.ikona.ikonaText?.length > 2) {
                errors.ikona = {ikonaText: 'Text může mít maximálně 2 znaky'};
            }
        } else if (!values.ikona.ikona) {
            errors.ikona = {ikona: 'Ikona je povinná'};
        }
        if (values.vytvoritLabelVrstvu && !values.labelSourceProperty) {
            errors.labelSourceProperty = 'Je třeba vybrat atribut pro popisky';
        }
        return errors;
    };

    const computedInitialValues = useMemo(() => {
        let vals = initialValues;
        if (!isEdit) {
            vals = {...vals, ikona: {typMarker: EnumIconType.CUSTOM}, publikovat: false, publikovatDokumenty: false};
        } // Pokud editujeme vrstvu typu polygon, nebo linie, chceme, aby byla možná pouze CUSTOM ICON
        else if (vals?.typGeometrie && (vals.typGeometrie === LayerGeometryType.POLYGON || vals.typGeometrie === LayerGeometryType.LINE)) {
            vals = {...vals, ikona: {...vals.ikona, typMarker: EnumIconType.CUSTOM}} as LayerSettingsVO;
        }
        if (xsdTyp) {
            //@ts-ignore
            vals = {...vals, xsdTyp};
        }
        if (vals) {
            const minScale = nearestScale(scales, resolutionToScale(vals.minResolution));
            const maxScale = nearestScale(scales, resolutionToScale(vals.maxResolution));
            vals = {
                ...vals,
                // pomocná pole pouze pro UI selectboxy
                // @ts-ignore
                minScale,
                // @ts-ignore
                maxScale,
            };
        }
        return vals;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const iconsQuery = useQuery<string[]>([CACHE_KEY_CATALOG_OPRAVNENI], async () => (await ConfigApi.findNonGeneratedIconsRelativePaths()).data, {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retryOnMount: false,
    });

    const atributyQuery = useQuery<AttributeConfigVO[]>(
        [CACHE_KEY_ATRIBUTY, computedInitialValues?.parentLayerId ?? computedInitialValues?.id],
        async () => (await AtributyApi.getAllAtribut(computedInitialValues?.parentLayerId ?? computedInitialValues?.id)).data,
        {enabled: isEdit && !!(computedInitialValues?.parentLayerId ?? computedInitialValues?.id)},
    );

    return (
        <Form<LayerSettingsVO>
            initialValues={computedInitialValues}
            onSubmit={values => {
                // @ts-ignore
                const minScale = values.minScale == null ? undefined : Number(values.minScale);
                // @ts-ignore
                const maxScale = values.maxScale == null ? undefined : Number(values.maxScale);
                const payload: LayerSettingsVO = {
                    ...values,
                    minResolution: Number.isFinite(minScale) ? scaleToResolution(minScale!) : undefined,
                    maxResolution: Number.isFinite(maxScale) ? scaleToResolution(maxScale!) : undefined,
                };
                // @ts-ignore
                delete payload.minScale;
                // @ts-ignore
                delete payload.maxScale;
                return onSubmit(payload);
            }}
            validate={validate}
        >
            {({submitting, values, handleSubmit, form: {change}}: FormRenderProps<LayerSettingsVO>) => {
                return (
                    <Dialog size="lg" onClose={onClose} title={title || (isEdit ? 'Úprava vrstvy' : 'Nová vrstva')}>
                        <DialogBody>
                            <Space vertical>
                                {!isSublayer && (
                                    <Card mode={'edit'}>
                                        <Block>
                                            <Row className="pb-0">
                                                <Col xs={13}>
                                                    <VF.String label={'Název vrstvy'} name={'nazev'} disabled={submitting} required />
                                                </Col>
                                                <Col className="mt-auto">
                                                    <VF.Checkbox label={'Publikovat'} name={'publikovat'} type={'checkbox'} />
                                                    <OnChange name={'publikovat'}>
                                                        {value => {
                                                            // Publikace dokumentů nemá bez publikace vrstvy smysl, ať nezůstane skrytě zapnutá.
                                                            if (!value) {
                                                                change('publikovatDokumenty', false);
                                                            }
                                                        }}
                                                    </OnChange>
                                                </Col>
                                                {values.publikovat && (
                                                    <Col className="mt-auto">
                                                        <VF.Checkbox
                                                            label={'Publikovat dokumenty'}
                                                            name={'publikovatDokumenty'}
                                                            type={'checkbox'}
                                                            disabled={submitting}
                                                        />
                                                    </Col>
                                                )}
                                            </Row>
                                            {xsdTyp && (
                                                <Row className="pt-0">
                                                    <Col xs={13}>
                                                        <VF.String label={'Xsd schéma'} name={'xsdTyp'} readOnly />
                                                    </Col>
                                                </Row>
                                            )}
                                        </Block>
                                    </Card>
                                )}
                                <Card mode={'edit'}>
                                    <Block>
                                        <Row>
                                            <Col>
                                                <Row>
                                                    <Col xs={'6'}>
                                                        <VF.SelectBox
                                                            name={'typGeometrie'}
                                                            items={getLayerGeometryTypeItems()}
                                                            label={'Typ geometrie'}
                                                            disabled={submitting || isEdit}
                                                            useIdAsValue
                                                            required
                                                        />
                                                    </Col>
                                                    <OnChange name={'typGeometrie'}>
                                                        {value => {
                                                            change(
                                                                'ikona',
                                                                value === LayerGeometryType.POINT
                                                                    ? {typMarker: EnumIconType.ICON}
                                                                    : {typMarker: EnumIconType.CUSTOM},
                                                            );
                                                        }}
                                                    </OnChange>
                                                </Row>

                                                <WaitForQueryData queries={[iconsQuery]}>
                                                    {() => (
                                                        <>
                                                            <Title type={'block'} className={'mt-1'}>
                                                                Ikona
                                                            </Title>
                                                            {values.typGeometrie === LayerGeometryType.POINT && (
                                                                <VF.RadioGroup name={'ikona.typMarker'} horizontal disabled={submitting}>
                                                                    <VF.Radio
                                                                        label="Ikona vrstvy"
                                                                        name="ikona.typMarker"
                                                                        value={EnumIconType.ICON}
                                                                        checked={EnumIconType.ICON === values?.ikona?.typMarker}
                                                                        disabled={submitting}
                                                                    />
                                                                    <VF.Radio
                                                                        label="Vlastní text"
                                                                        name="ikona.typMarker"
                                                                        value={EnumIconType.CUSTOM}
                                                                        checked={EnumIconType.CUSTOM === values?.ikona?.typMarker}
                                                                        disabled={submitting}
                                                                    />
                                                                </VF.RadioGroup>
                                                            )}
                                                            <OnChange name={'ikona.typMarker'}>
                                                                {() => {
                                                                    // @ts-ignore
                                                                    change('ikona.ikonaText', undefined);
                                                                    // @ts-ignore
                                                                    change('ikona.ikona', undefined);
                                                                    // @ts-ignore
                                                                    change('ikona.ikonaSelect', undefined);
                                                                }}
                                                            </OnChange>

                                                            {values?.ikona?.typMarker === EnumIconType.CUSTOM ? (
                                                                <Row>
                                                                    <Col xs={'5'}>
                                                                        <VF.String
                                                                            name={'ikona.ikonaText'}
                                                                            label={'Text ikony (1-2 znaky)'}
                                                                            disabled={submitting}
                                                                            required
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            ) : (
                                                                <>
                                                                    <Row>
                                                                        <Col xs={'7'}>
                                                                            <VF.SelectBox
                                                                                items={iconsQuery.data}
                                                                                getItemName={(x: string) => x || ''}
                                                                                getItemId={(x: string) => x || ''}
                                                                                renderItemContent={(x: string) =>
                                                                                    x && (
                                                                                        <div className={'py-1 px-2'}>
                                                                                            <img
                                                                                                className={'mr-1'}
                                                                                                width={'20'}
                                                                                                height={'20'}
                                                                                                alt={x}
                                                                                                src={
                                                                                                    appConfig.serverContextPath +
                                                                                                    '/api/files/img/' +
                                                                                                    x.replace('files/img/', '')
                                                                                                }
                                                                                            />
                                                                                            {x}
                                                                                        </div>
                                                                                    )
                                                                                }
                                                                                name={'ikona.ikona'}
                                                                                label={'Ikona'}
                                                                                useIdAsValue
                                                                                disabled={submitting}
                                                                            />
                                                                            <OnChange name={'ikona.ikona'}>
                                                                                {value => {
                                                                                    if (values?.ikona?.ikonaSelect) return;
                                                                                    //@ts-ignore
                                                                                    change('ikona.ikonaSelect', value);
                                                                                }}
                                                                            </OnChange>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col xs={'7'}>
                                                                            <VF.SelectBox
                                                                                items={iconsQuery.data}
                                                                                getItemName={(x: string) => x || ''}
                                                                                getItemId={(x: string) => x || ''}
                                                                                renderItemContent={(x: string) =>
                                                                                    x && (
                                                                                        <div className={'py-1 px-2'}>
                                                                                            <img
                                                                                                className={'mr-1'}
                                                                                                width={'20'}
                                                                                                height={'20'}
                                                                                                alt={x}
                                                                                                src={
                                                                                                    appConfig.serverContextPath +
                                                                                                    '/api/files/img/' +
                                                                                                    x.replace('files/img/', '')
                                                                                                }
                                                                                            />
                                                                                            {x}
                                                                                        </div>
                                                                                    )
                                                                                }
                                                                                name={'ikona.ikonaSelect'}
                                                                                label={'Ikona pro výběr'}
                                                                                useIdAsValue
                                                                                required
                                                                                disabled={submitting}
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            )}
                                                            {values?.ikona?.typMarker === EnumIconType.CUSTOM && (
                                                                <>
                                                                    <Title type={'block'}>
                                                                        Barva <span className={'manual-required'}>*</span>
                                                                    </Title>
                                                                    <ColorPicker name={'barva'} disabled={submitting} items={COLOR_ITEMS} required />
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </WaitForQueryData>
                                            </Col>
                                            <Col xs={'auto'}>
                                                <IkonPreview
                                                    typGeometrie={values.typGeometrie}
                                                    barva={values.barva}
                                                    ikonaText={values.ikona?.ikonaText}
                                                    ikona={values.ikona?.ikona}
                                                    typMarker={values.ikona?.typMarker}
                                                    labelEnabled={values.vytvoritLabelVrstvu}
                                                    labelText={'Popisek'}
                                                    labelFontSize={values.labelFontSize}
                                                    labelFontFamily={values.labelFontFamily}
                                                    labelFontBold={values.labelFontBold}
                                                    labelFontColor={values.labelFontColor}
                                                    labelStrokeColor={values.labelStrokeColor}
                                                    labelOffsetX={values.labelOffsetX as number}
                                                    labelOffsetY={values.labelOffsetY as number}
                                                />
                                            </Col>
                                        </Row>
                                    </Block>
                                </Card>
                                {isEdit && (
                                    <WaitForQueryData queries={[atributyQuery]}>
                                        {() => (
                                            <Card mode={'edit'}>
                                                <Block>
                                                    <Title type={'block'}>Popisky v mapě</Title>
                                                    <Row>
                                                        <Col>
                                                            <VF.Checkbox
                                                                disabled={submitting}
                                                                label={'Zapnout popisky'}
                                                                name={'vytvoritLabelVrstvu'}
                                                                type={'checkbox'}
                                                                inlineValidation
                                                            />
                                                            <OnChange name={'vytvoritLabelVrstvu'}>
                                                                {() => {
                                                                    change('maxResolution', 1.98);
                                                                    // @ts-ignore maxScale je pouze lokální pomocné pole
                                                                    change('maxScale', nearestScale(scales, resolutionToScale(1.98)));
                                                                    change('labelFontSize', '10');
                                                                    change('labelFontColor', values.barva);
                                                                    change('labelStrokeColor', 'FFFFFF');
                                                                }}
                                                            </OnChange>
                                                        </Col>
                                                    </Row>
                                                    {values.vytvoritLabelVrstvu && (
                                                        <>
                                                            {atributyQuery.isFetched && !atributyQuery.data?.length ? (
                                                                <Callout intent="info" className={'mb-2'}>
                                                                    Pro nastavení popisné vrstvy je třeba mít vytvořen atribut, který bude použit jako zdroj pro
                                                                    popisky.
                                                                </Callout>
                                                            ) : (
                                                                <>
                                                                    <Row>
                                                                        <Col xs={8}>
                                                                            <VF.SelectBox
                                                                                name={'labelSourceProperty'}
                                                                                label={'Atribut pro popisek'}
                                                                                items={
                                                                                    atributyQuery.data?.filter(i => i.typ !== EnumTypLayerAttr.BOOLEAN) || []
                                                                                }
                                                                                getItemId={(item: AttributeConfigVO) => item?.nazev || ''}
                                                                                getItemName={(item: AttributeConfigVO) => item?.popisek || ''}
                                                                                useIdAsValue
                                                                                disabled={submitting || atributyQuery.isLoading}
                                                                                required
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                    <ColorPicker
                                                                        name={'labelFontColor'}
                                                                        disabled={submitting}
                                                                        label={'Barva písma popisku'}
                                                                        items={['null', ...COLOR_ITEMS]}
                                                                    />
                                                                    <Row className="pt-1">
                                                                        <Col>
                                                                            <Checkbox
                                                                                label={'Pokročilé nastavení popisku'}
                                                                                style={{gap: '10px'}}
                                                                                onChange={() => setShowAdvanced(!showAdvanced)}
                                                                                disabled={submitting}
                                                                            />
                                                                        </Col>
                                                                    </Row>
                                                                    {showAdvanced && (
                                                                        <>
                                                                            <Divider className="pb-1" horizontal />
                                                                            <ColorPicker
                                                                                name={'labelStrokeColor'}
                                                                                disabled={submitting}
                                                                                label={'Barva obrysu popisku'}
                                                                                items={['null', ...COLOR_ITEMS]}
                                                                            />
                                                                            <Row>
                                                                                <Col xs={5}>
                                                                                    <VF.SelectBox
                                                                                        name={'minScale'}
                                                                                        label={'Minimální měřítko zobrazení'}
                                                                                        items={scaleItems}
                                                                                        useIdAsValue
                                                                                        disabled={submitting || scaleItems.length === 0}
                                                                                    />
                                                                                </Col>
                                                                                <Col xs={5}>
                                                                                    <VF.SelectBox
                                                                                        name={'maxScale'}
                                                                                        label={'Maximální měřítko zobrazení'}
                                                                                        items={scaleItems}
                                                                                        useIdAsValue
                                                                                        disabled={submitting || scaleItems.length === 0}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                            <Row>
                                                                                <Col xs={5}>
                                                                                    <VF.String
                                                                                        name={'labelFontSize'}
                                                                                        label={'Velikost fontu'}
                                                                                        disabled={submitting}
                                                                                    />
                                                                                </Col>
                                                                                <Col xs={5}>
                                                                                    <VF.SelectBox
                                                                                        name={'labelFontFamily'}
                                                                                        label={'Rodina fontu'}
                                                                                        items={[
                                                                                            {id: 'arial', name: 'Arial'},
                                                                                            {id: 'courier new', name: 'Courier New'},
                                                                                            {id: 'cursive', name: 'Cursive'},
                                                                                            {id: 'garamond', name: 'Garamond'},
                                                                                            {id: 'georgia', name: 'Georgia'},
                                                                                            {id: 'helvetica', name: 'Helvetica'},
                                                                                            {id: 'monospace', name: 'Monospace'},
                                                                                            {id: 'sans-serif', name: 'Sans-serif'},
                                                                                            {id: 'serif', name: 'Serif'},
                                                                                            {id: 'tahoma', name: 'Tahoma'},
                                                                                            {id: 'times new roman', name: 'Times New Roman'},
                                                                                            {id: 'trebuchet ms', name: 'Trebuchet MS'},
                                                                                            {id: 'verdana', name: 'Verdana'},
                                                                                        ]}
                                                                                        useIdAsValue
                                                                                        disabled={submitting}
                                                                                    />
                                                                                </Col>
                                                                                <Col className="mt-auto">
                                                                                    <VF.Checkbox
                                                                                        label={'Tučně'}
                                                                                        name={'labelFontBold'}
                                                                                        type={'checkbox'}
                                                                                        disabled={submitting}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                            <Row>
                                                                                <Col xs={5}>
                                                                                    <VF.Number
                                                                                        name={'labelOffsetX'}
                                                                                        label={'Odsazení X'}
                                                                                        disabled={submitting}
                                                                                    />
                                                                                </Col>
                                                                                <Col xs={5}>
                                                                                    <VF.Number
                                                                                        name={'labelOffsetY'}
                                                                                        label={'Odsazení Y'}
                                                                                        disabled={submitting}
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </Block>
                                            </Card>
                                        )}
                                    </WaitForQueryData>
                                )}
                            </Space>
                        </DialogBody>
                        <DialogFooter>
                            <Button style="default" onClick={onClose} disabled={submitting}>
                                Zrušit
                            </Button>
                            <Button style="primary" onClick={handleSubmit} disabled={submitting}>
                                {isEdit ? 'Uložit' : 'Vytvořit'}
                            </Button>
                        </DialogFooter>
                    </Dialog>
                );
            }}
        </Form>
    );
};
